import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

const AUTH_BG = 'https://images.unsplash.com/photo-1650513973337-846467d29925?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NjA4ODA4Nnww&ixlib=rb-4.1.0&q=85';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 to-slate-950/40" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="text-4xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            GO MO Group DBS
          </h1>
          <p className="text-slate-300 text-sm max-w-md">
            Digital Business Solutions — Finance Management Portal
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md bg-card/80 border-border" data-testid="auth-card">
          <CardHeader className="space-y-1">
            <div className="lg:hidden mb-4">
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>GO MO Group DBS</h2>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Digital Business Solutions</p>
            </div>
            <CardTitle className="text-xl text-card-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Sign in to your account
            </CardTitle>
            <CardDescription className="text-muted-foreground text-sm">
              Enter your credentials to access the portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-card-foreground/80 text-sm">Email</Label>
                <Input id="email" type="email" data-testid="email-input" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" required
                  className="bg-[hsl(var(--surface-input))] border-input focus:border-blue-500 text-card-foreground placeholder:text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-card-foreground/80 text-sm">Password</Label>
                <Input id="password" type="password" data-testid="password-input" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password" required
                  className="bg-[hsl(var(--surface-input))] border-input focus:border-blue-500 text-card-foreground placeholder:text-muted-foreground" />
              </div>
              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="auth-error">
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading} data-testid="auth-submit-button">
                {loading ? <Loader2 className="animate-spin" size={16} /> : 'Sign In'}
              </Button>
            </form>
            <p className="mt-4 text-center text-xs text-muted-foreground">Contact your administrator for account access</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
